import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { NAMESPACES } from "@/i18n/constants";
import type { CLOCK_CADENCE } from "@/providers/wall-clock";
import type { DashboardSpec } from "./ChartSpec";
import { DataViewerBody } from "./data-viewer-body";
import { DataViewerProvider } from "./data-viewer-provider";
import { SpecProvider } from "./spec/spec-context";

function DataViewerMessage({ message }: { message: string }) {
    return (
        <Card>
            <div className={"text-muted-foreground text-sm text-center"}>{message}</div>
        </Card>
    );
}

export function DataViewer({
    initialSpec,
    isLoading,
    isError,
    isEmpty,
    autoRefreshCadence,
    resizable = true,
    controls,
    baseHeightPx,
}: {
    initialSpec: DashboardSpec;
    isLoading?: boolean;
    isError?: boolean;
    isEmpty?: boolean;
    autoRefreshCadence?: CLOCK_CADENCE;
    /** When false, panels fill available width at default height with no drag grips. */
    resizable?: boolean;
    /**
     * Rendered in the toolbar before the time range picker, inside the providers. The expanded
     * dialog has its own toolbar, so this renders twice at once and must be safe that way — keep
     * its state lifted into the card, as `PanelLayoutToggle` does.
     */
    controls?: ReactNode;
    /** The base height of each panel, in pixels */
    baseHeightPx?: number;
}) {
    const { t } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "messages" });

    if (isLoading) {
        return <DataViewerMessage message={t("loading")} />;
    }
    if (isError) {
        return <DataViewerMessage message={t("load-failed")} />;
    }
    if (isEmpty) {
        return <DataViewerMessage message={t("empty")} />;
    }

    return (
        <SpecProvider initialSpec={initialSpec}>
            <DataViewerProvider defaultTimeRange={initialSpec.defaultTimeRange}>
                <DataViewerBody
                    autoRefreshCadence={autoRefreshCadence}
                    baseHeightPx={baseHeightPx}
                    controls={controls}
                    resizable={resizable}
                />
            </DataViewerProvider>
        </SpecProvider>
    );
}
